import { Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { IChatProduct } from "@models/chat.model";
import { ChatService } from "@services/chat/chat.service";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { ChatImageComponent } from "./chat-image.component";

/** Pick one of the store's products to send as a product card. */
@Component({
  selector: "fh-product-picker-dialog",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ChatImageComponent,
  ],
  template: `
    <h2 mat-dialog-title>Send a product</h2>
    <mat-dialog-content>
      <mat-form-field appearance="outline" class="search">
        <mat-label>Search your products</mat-label>
        <input matInput [formControl]="searchControl" placeholder="Product name" />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      @if (loading()) {
        <div class="state"><mat-spinner diameter="28"></mat-spinner></div>
      } @else if (error()) {
        <p class="state error">{{ error() }}</p>
      } @else if (!products().length) {
        <p class="state">No products found.</p>
      } @else {
        <div class="list">
          @for (product of products(); track product.id) {
            <button type="button" class="row" (click)="pick(product)">
              <fh-chat-image
                class="thumb"
                [path]="product.productImage"
                fallbackIcon="image"
                [alt]="product.productName"
              />
              <span class="info">
                <span class="name">{{ product.productName }}</span>
                @if (product.qty > 0) {
                  <span class="qty">{{ product.qty }} in stock</span>
                } @else {
                  <span class="qty out">Out of stock</span>
                }
              </span>
              <mat-icon class="send">send</mat-icon>
            </button>
          }
        </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      /* 16px text in the fields: iOS zooms in on anything smaller when tapped. */
      .mat-mdc-input-element {
        font-size: 16px;
      }

      .search {
        width: 100%;
      }
      .state {
        display: flex;
        justify-content: center;
        padding: 16px 0;
        color: rgba(0, 0, 0, 0.6);
      }
      .error {
        color: #c62828;
      }
      .list {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .row {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 8px;
        border: none;
        border-radius: 8px;
        background: none;
        text-align: left;
        cursor: pointer;
      }
      .row:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
      .thumb {
        width: 48px;
        height: 48px;
        border-radius: 6px;
      }
      .info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
      }
      .name {
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .qty {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
      }
      .qty.out {
        color: #c62828;
      }
      .send {
        color: #1976d2;
      }
    `,
  ],
})
export class ProductPickerDialogComponent implements OnInit, OnDestroy {
  private chatService = inject(ChatService);
  private dialogRef = inject(MatDialogRef<ProductPickerDialogComponent, IChatProduct>);

  searchControl = new FormControl<string>("", { nonNullable: true });
  products = signal<IChatProduct[]>([]);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  private searchSubs?: Subscription;
  private requestSubs?: Subscription;

  ngOnInit(): void {
    this.load("");
    this.searchSubs = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((search) => this.load(search.trim()));
  }

  ngOnDestroy(): void {
    this.searchSubs?.unsubscribe();
    this.requestSubs?.unsubscribe();
  }

  pick(product: IChatProduct): void {
    this.dialogRef.close(product);
  }

  private load(search: string): void {
    this.requestSubs?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);
    this.requestSubs = this.chatService.products(search).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.status) this.products.set(res.data?.products ?? []);
        else this.error.set(res.message || "Could not load products.");
      },
      error: () => {
        this.loading.set(false);
        this.error.set("Could not load products.");
      },
    });
  }
}
